import { AnimatedNumber } from "./AnimatedNumber";
import { CurrencyAmount } from "./CurrencyAmount";
import { DEFAULT_CURRENCY } from "../lib/defaultCurrency";
import { ArrowUpRight, Calendar, TrendingUp, Wallet, Receipt } from "lucide-react";

function formatDate(iso) {
    try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
    catch (_e) { return iso; }
}

function PoolOverviewHeader({ pool, onCreateInvoice }) {
    return (
            <div className="flex flex-wrap items-center gap-3 fs-pill-anchor">
                <div className="flex items-center gap-2.5 fs-pill-anchor">
                    <span className="status-dot" aria-hidden />
                    <h2 className="font-heading text-2xl sm:text-3xl tracking-tight">{pool?.name || "Unnamed pool"}</h2>
                </div>
                <span className="fs-pill rounded-full border border-[#00BF0D]/25 bg-[#00BF0D]/10 px-2.5 py-0.5 text-[10px] font-semibold tracking-[0.15em] text-[#00BF0D]" data-testid="pool-status-badge">
                    {pool.status?.toUpperCase()}
                </span>
                <span className="fs-pill rounded-full border border-white/[0.08] bg-white/[0.025] px-2.5 py-0.5 text-[10px] font-semibold tracking-[0.15em] text-white/65 capitalize">
                    {pool.cadence}
                </span>
                {pool.description && <span className="text-xs text-white/40 truncate max-w-md">{pool.description}</span>}
                {onCreateInvoice && (
                    <button
                        onClick={onCreateInvoice}
                        className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-white/[0.08] bg-white/[0.025] hover:bg-white/[0.05] px-3 py-1.5 text-xs font-medium transition-colors"
                        data-testid="bill-client-button"
                        data-testid-alt="stripe-invoice-button"
                    >
                        <Receipt className="h-3.5 w-3.5 text-[#00BF0D]" />
                        Bill a client
                    </button>
                )}
            </div>
    );
}

function PoolOverviewMetrics({ pool }) {
    return (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="surface p-4 sm:p-5" data-testid="card-total-revenue">
                    <div className="flex items-center justify-between">
                        <p className="label-eyebrow">Total Revenue</p>
                        <TrendingUp className="h-3.5 w-3.5 text-[#00BF0D]/70" />
                    </div>
                    <p className="font-heading text-3xl sm:text-4xl mt-2 text-[#00BF0D] font-mono tracking-tight tabular-nums min-h-[3.5rem]">
                        <CurrencyAmount amount={pool.total_revenue || 0} currency={pool.currency || DEFAULT_CURRENCY} decimals={2} />
                    </p>
                    <p className="text-xs text-white/40 mt-1.5">Available pool balance</p>
                </div>
                <div className="surface p-4 sm:p-5" data-testid="card-last-payout">
                    <div className="flex items-center justify-between">
                        <p className="label-eyebrow">Last Payout</p>
                        <Wallet className="h-3.5 w-3.5 text-white/40" />
                    </div>
                    <p className="font-heading text-3xl sm:text-4xl mt-2 text-white font-mono tracking-tight tabular-nums min-h-[3.5rem]">
                        <CurrencyAmount amount={pool.last_payout?.amount || 0} currency={pool.currency || DEFAULT_CURRENCY} decimals={2} />
                    </p>
                    <p className="text-xs text-white/45 mt-1.5 inline-flex items-center gap-1">
                        <ArrowUpRight className="h-3 w-3" />
                        {pool.last_payout?.date ? `Disbursed ${formatDate(pool.last_payout.date)}` : "No payouts yet"}
                    </p>
                </div>
                <div className="surface p-4 sm:p-5" data-testid="card-next-payout">
                    <div className="flex items-center justify-between">
                        <p className="label-eyebrow">Next Scheduled</p>
                        <Calendar className="h-3.5 w-3.5 text-white/40" />
                    </div>
                    <p className="font-heading text-3xl sm:text-4xl mt-2 text-white font-mono tracking-tight tabular-nums min-h-[3.5rem]">
                        <CurrencyAmount amount={pool.next_payout?.amount || 0} currency={pool.currency || DEFAULT_CURRENCY} decimals={2} />
                    </p>
                    <p className="text-xs text-white/45 mt-1.5 inline-flex items-center gap-1 capitalize">
                        <Calendar className="h-3 w-3" />
                        {pool.next_payout?.date ? formatDate(pool.next_payout.date) : "—"} · {pool.cadence}
                    </p>
                </div>
            </div>
    );
}

/** @param {"all" | "header" | "metrics"} [sections] — split layout on desktop dashboard */
export function PoolOverview({ pool, onCreateInvoice, sections = "all", className = "" }) {
    const showHeader = sections === "all" || sections === "header";
    const showMetrics = sections === "all" || sections === "metrics";

    return (
        <section className={`space-y-4 ${className}`.trim()} data-testid="pool-overview">
            {showHeader && <PoolOverviewHeader pool={pool} onCreateInvoice={onCreateInvoice} />}
            {showMetrics && <PoolOverviewMetrics pool={pool} />}
        </section>
    );
}
